/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Julia Ignatieva
 * @author Alexander Golshtein
 * @author Evgeniy Naumov
 * @version 1.25.0
 * @since 1.0.0
 */

import { FC, ReactElement, ReactNode } from 'react';

import { useSelector } from 'react-redux';

import { ONBOARDING_DATA_ATTR } from '@/main/modules/onboarding/onboardingConstants';
import { ELinkType } from '@components/common/InstructionLink/types';
import { TInstructionLink } from '@modules/internationalization/types/corporate';
import { EOnboardingComponentsId } from '@modules/onboarding/types/onboardingTypes';
import { FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectFeatureEnabled } from '@pages/PageAuth/selectors/authSelectors';

import { LayoutStyled } from '../Block/Layout';
import { BlockWithIcon, Button, DropFiltersButton, InstructionLink } from '../index';
import {
    Counter,
    PageContainerStyled,
    PagePanelButtonTextStyled,
    SettingsStyled,
    TitleDivStyled,
    titleIconSize,
    TitleStyled,
} from './styled/PageTitleStyled';


export interface IPageTitleProps {
    count?: number;
    dataName?: string;
    subTitle?: string;
    title?: string;
    titleOnboarding?: EOnboardingComponentsId;
    subTitleOnboarding?: EOnboardingComponentsId;
    description?: string;
    instructionLink?: TInstructionLink;
}

export interface IPageTitleSettingButtonProps {
    className?: string;
    component?: ReactElement;
    dataName?: string;
    iconLeft?: string;
    iconRight?: string;
    id: number;
    onboarding?: EOnboardingComponentsId;
    onClick?: (e?: any) => void;
    value?: string;
}

export interface IPageTitleDropButtonProps {
    className?: string;
    onClick?: () => void;
    value: string;
}

const PageTitle: FC<IPageTitleProps> = (props) => {

    const isInstructionsLinksEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.INSTRUCTIONS_LINKS));
    return (
        <PageContainerStyled data-page-title={props.dataName}>
            {!!props.title && (
                <div>
                    <TitleDivStyled>
                        <TitleStyled {...{ [ONBOARDING_DATA_ATTR]: props.titleOnboarding }}>
                            {props.title}
                            {props.count !== undefined && <Counter data-counter="counter">{props.count}</Counter>}
                        </TitleStyled>
                        {!!props.instructionLink && isInstructionsLinksEnabled && (
                            <InstructionLink {...{ ...props.instructionLink, linkType: ELinkType.TEXT }}/>
                        )}
                    </TitleDivStyled>
                    {!!props.subTitle && (
                        <LayoutStyled className="mt-1" {...{ [ONBOARDING_DATA_ATTR]: props.subTitleOnboarding }}>
                            {props.subTitle}
                        </LayoutStyled>
                    )}
                    {
                        !!props.description && (
                            <LayoutStyled className="mt-1">{props.description}</LayoutStyled>
                        )
                    }
                </div>
            )}
            <SettingsStyled>{props.children}</SettingsStyled>
        </PageContainerStyled>
    )
};

export default PageTitle;

/**
 * @description Render setting button in page title
 * @param {IPageTitleSettingButtonProps} props
 * @return {ReactNode}
 */
export const renderSettingButton = (props: IPageTitleSettingButtonProps): ReactNode => (
    <Button
        className={`page-title__button ${props.className || ''}`}
        dataName={props.dataName}
        key={props.id}
        kind="table"
        onboarding={props.onboarding}
        onClick={props.onClick}
    >
        <BlockWithIcon
            className="page-title__block-with-icon"
            iconLeft={props.iconLeft}
            iconLeftSize={titleIconSize}
            iconRight={props.iconRight}
            iconRightSize={titleIconSize}
        >
            <PagePanelButtonTextStyled>{props.value}</PagePanelButtonTextStyled>
        </BlockWithIcon>
    </Button>
);

/**
 * @description Render drop filter button in page title
 * @param {IPageTitleDropButtonProps} props
 * @return {ReactNode}
 */
export const renderDropFilterButton = (props: IPageTitleDropButtonProps): ReactNode => (
    <DropFiltersButton dataName="table-clear-filter" label={props.value} onClick={props.onClick} />
);
